import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import ReactDOM from 'react-dom';
import { ownerDocument } from '../utils/dom';
import { useForkRef, setRef } from '../utils/withForwardedRef';
import useEventCallback from '../utils/useEventCallback';

function mapEventPropToEvent(eventProp) {
  return eventProp.substring(2).toLowerCase();
}

var ClickAwayListener = React.forwardRef(function (props, ref) {
  var children = props.children,
      _a = props.mouseEvent,
      mouseEvent = _a === void 0 ? 'onClick' : _a,
      _b = props.touchEvent,
      touchEvent = _b === void 0 ? 'onTouchEnd' : _b,
      onClickAway = props.onClickAway;
  var movedRef = React.useRef(false);
  var nodeRef = React.useRef(null);
  var activatedRef = React.useRef(false);
  var syntheticEventRef = React.useRef(false);
  React.useEffect(function () {
    // Ensure that this component is not "activated" synchronously.
    // https://github.com/facebook/react/issues/20074
    setTimeout(function () {
      activatedRef.current = true;
    }, 0);
    return function () {
      activatedRef.current = false;
    };
  }, []);
  var handleNodeRef = useForkRef(nodeRef, ref); // can be removed once we drop support for non ref forwarding class components

  var handleOwnRef = React.useCallback(function (instance) {
    // #StrictMode ready
    setRef(handleNodeRef, ReactDOM.findDOMNode(instance));
  }, [handleNodeRef]);
  var handleRef = useForkRef(children.ref || null, handleOwnRef);
  var handleClickAway = useEventCallback(function (event) {
    var insideReactTree = syntheticEventRef.current;
    syntheticEventRef.current = false; // Ignore events that have been `event.preventDefault()` marked.

    if (event.defaultPrevented) {
      return;
    } // IE 11 support, which trigger the handleClickAway even after the unbind


    if (!activatedRef.current) {
      return;
    } // Do not act if user performed touchmove


    if (movedRef.current) {
      movedRef.current = false;
      return;
    } // The child might render null.


    if (!nodeRef.current) {
      return;
    } // Multi window support


    var doc = ownerDocument(nodeRef.current);

    if (doc.documentElement && doc.documentElement.contains(event.target) && !nodeRef.current.contains(event.target) && !insideReactTree) {
      onClickAway(event);
    }
  });
  var handleTouchMove = React.useCallback(function () {
    movedRef.current = true;
  }, []);
  var childrenProps = {
    ref: handleRef
  };

  var createHandleSynthetic = function createHandleSynthetic(handlerName) {
    return function (event) {
      syntheticEventRef.current = true;
      var childrenPropsHandler = children.props[handlerName];

      if (childrenPropsHandler) {
        childrenPropsHandler(event);
      }
    };
  };

  if (touchEvent !== false) {
    childrenProps[touchEvent] = createHandleSynthetic(touchEvent);
  }

  React.useEffect(function () {
    if (touchEvent !== false) {
      var mappedTouchEvent_1 = mapEventPropToEvent(touchEvent);
      document.addEventListener(mappedTouchEvent_1, handleClickAway);
      document.addEventListener('touchmove', handleTouchMove);
      return function () {
        document.removeEventListener(mappedTouchEvent_1, handleClickAway);
        document.removeEventListener('touchmove', handleTouchMove);
      };
    }

    return undefined;
  }, [handleClickAway, handleTouchMove, touchEvent]);

  if (mouseEvent !== false) {
    childrenProps[mouseEvent] = createHandleSynthetic(mouseEvent);
  }

  React.useEffect(function () {
    if (mouseEvent !== false) {
      var mappedMouseEvent_1 = mapEventPropToEvent(mouseEvent);
      document.addEventListener(mappedMouseEvent_1, handleClickAway);
      return function () {
        document.removeEventListener(mappedMouseEvent_1, handleClickAway);
      };
    }

    return undefined;
  }, [handleClickAway, mouseEvent]);
  return _jsx(_Fragment, {
    children: React.cloneElement(children, childrenProps)
  });
});
export default ClickAwayListener;